import React, { lazy, useState, useEffect, useRef } from 'react'
import { FiSearch, FiHeart, FiCreditCard, FiBarChart2, FiActivity } from 'react-icons/fi'
import { useTranslation } from './i18n/LanguageContext'
import SearchFilters, { emptyFilters } from './components/SearchFilters'
import AppUpdateNotice from './components/AppUpdateNotice.jsx'
import SavedListingFeedback, { useSavedListings } from './components/SavedListingFeedback.jsx'
import ResultsList from './components/ResultsList'
import DeferredSection from './components/DeferredSection.jsx'
import SavedPlans, { useSavedPlans } from './components/SavedPlans.jsx'
import SavedKentekenDrafts, { useKentekenDrafts } from './components/KentekenDrafts.jsx'
import { searchCarListings, searchAllCarListings } from './lib/backend'
import { servedTruthForDisplay } from './lib/servedEconomics.js'
import { recommendationEvidenceFields } from './lib/recommendationEvidence.js'
import { createSearchSession } from './lib/searchSession.js'
import { adLink } from './lib/picks.js'
import './App.css'

// TAB-LOAD-1: the first search does not need the analysis/plate UI. Load on navigation.
const LegalDisclaimer = lazy(() => import('./components/LegalDisclaimer.jsx'))
const KentekenLookup = lazy(() => import('./components/KentekenLookup.jsx'))
const MarketAnalytics = lazy(() => import('./components/MarketAnalytics.jsx'))
const Scorecard = lazy(() => import('./components/Scorecard.jsx'))

const PAGE_SIZE = 25
// SIMPLE-1: the lists each view shows, in order. Each is one server query, paged by "Show more".
const LISTS = { picks: ['koopkans', 'controleer'], all: ['all'] }
const numberOrNull = value => (value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? null : Number(value))

/** A served row as the card, the map and a saved snapshot read it: flat scalars only. Economics
 * come from servedTruthForDisplay, which refuses to show P&L for a row that breaks the served
 * identities, so a malformed row stays visible without invented numbers. */
export function toCard(car, index = 0) {
  const truth = servedTruthForDisplay(car)
  const image = car.image_link && !String(car.image_link).includes('placeholder') ? car.image_link : null
  return {
    ...truth,
    id: car.listing_id ? `${car.country_code}-${car.listing_id}` : `row-${index}`,
    recommendation_evidence: recommendationEvidenceFields(car),
    make: car.make || car.brand, model: car.model,
    variant: car.variant || '', variant_source: car.variant_source || '',
    year: Number(car.year) || 0,
    mileage: Number(car.mileage_km) || 0, mileage_km: car.mileage_km,
    power: Number(car.horsepower_hp) || 0, horsepower_hp: car.horsepower_hp,
    fuel: car.fuel_type, fuel_type: car.fuel_type,
    transmission: car.transmission_norm || car.transmission,
    country_code: car.country_code, location: car.location, image,
    seller_type: car.seller_type,
    days_online: car.days_online_timestamp
      ? Math.max(0, Math.ceil((Date.now() - new Date(car.days_online_timestamp).getTime()) / 86400000))
      : Number(car.days_online) || 0,
    listing_id: car.listing_id ? Number(car.listing_id) : null,
    scraped_at: car.scraped_at, source_checked_at: car.source_checked_at || null,
    source_flag: car.source_flag || null, source_real_url: car.source_real_url || null,
    detail_page_url: car.detail_page_url || null, external_link: car.external_link || null,
    decision_snapshot_id: car.decision_snapshot_id || null, model_git_sha: car.model_git_sha || null,
    pred_status: car.prediction_status,
    match_count: Number(car.match_count) || 0, match_story: car.match_story || null,
    // A pick whose economics fail the served identities is not shown as a pick.
    buy_score: truth.economics_valid ? (Number(car.buy_score) || 0) : 0,
    buy_reason: car.buy_reason || null,
    expected_margin_net_pct: numberOrNull(car.expected_margin_net_pct),
    min_margin_required: numberOrNull(car.min_margin_required),
    price_flag_anomaly: car.price_flag_anomaly || false,
    sale_haircut_eur: numberOrNull(car.sale_haircut_eur),
    condition_reserve_eur: numberOrNull(car.condition_reserve_eur),
    source_price: numberOrNull(car.source_price),
    profit_expected_eur: numberOrNull(car.profit_expected_eur),
    profit_safe_eur: numberOrNull(car.profit_safe_eur),
    bpm_calculation_notes: car.bpm_calculation_notes || null, bpm_co2_source: car.bpm_co2_source || null,
    bpm_co2_original: numberOrNull(car.bpm_co2_original), bpm_gross: Number(car.bpm_gross) || 0,
    bpm_depr_pct: Number(car.bpm_depr_pct) || 0,
    transport_location_lat: numberOrNull(car.transport_location_lat),
    transport_location_lon: numberOrNull(car.transport_location_lon),
    // Confidence in the valuation and what the pricing popup explains.
    confidence_score: numberOrNull(car.prediction_confidence_score) || 0,
    expected_ape_pct: numberOrNull(car.expected_ape_pct), expected_error_pct: numberOrNull(car.expected_error_pct),
    match_tier: displayTier(car.match_tier), match_tier_raw: car.match_tier || 'NA',
    n: Number(car.match_count) || Number(car.n_similar_cars_found) || 0,
    pricing_explanation_html: car.pricing_explanation_html || null,
    has_nl_valuation: Number(car.pred_target_price) > 0,
    // The planner and the pricing popup re-check the served identities on the raw names.
    price_amount_eur: truth.price_it,
    condition_reserve_pct_of_price: numberOrNull(car.condition_reserve_pct_of_price),
    // How long comparable cars sit online (the P&L's listing-age block).
    est_days_to_sell: numberOrNull(car.est_days_to_sell),
    model_days_median: numberOrNull(car.model_days_median), model_days_count: Number(car.model_days_count) || 0,
    // The spec list, as a car site shows it.
    horsepower_kw: numberOrNull(car.horsepower_kw), engine_l: numberOrNull(car.engine_l),
    body_type: car.car_type || '', co2: Number(car.emission_co2_g_km) || 0,
    platform: car.platform || null, listed_since: car.listed_since || null,
    price_change_percent: numberOrNull(car.price_change_percent), deal_category: car.deal_category || null,
    transport_distance_km: numberOrNull(car.transport_distance_km),
    price_amount_original: numberOrNull(car.price_amount_original),
    price_currency_original: car.price_currency_original || null,
    model_rarity_score: numberOrNull(car.model_rarity_score), model_listing_count: numberOrNull(car.model_listing_count),
    // The purchase planner's memo names the advert it is about.
    detail_url: adLink(car)?.href || null,
  }
}

/** 'tier1' -> 'Tier 1', 'continuous' -> 'Continuous', missing -> 'NA' (the card's tier chip). */
function displayTier(tier) {
  const raw = String(tier || '')
  if (!raw || raw.toLowerCase() === 'na') return 'NA'
  if (raw.toLowerCase() === 'continuous') return 'Continuous'
  const level = raw.match(/^tier(\d+\w*)$/i)
  return level ? `Tier ${level[1]}` : raw
}

function App() {
  const { t, lang, toggleLang } = useTranslation()
  const searchSession = useRef(null)
  if (!searchSession.current) searchSession.current = createSearchSession()
  useEffect(() => () => searchSession.current.invalidate(), [])

  const [draft, setDraft] = useState(emptyFilters)
  const [filters, setFilters] = useState(emptyFilters)
  const [sortBy, setSortBy] = useState('safe')
  const [lists, setLists] = useState({})          // key -> { rows, total, page }
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [currentView, setCurrentView] = useState('search')
  const [plateDraft, setPlateDraft] = useState(null)
  const { savedListings, savedListingIds, toggleSaveListing, feedback, dismissFeedback } = useSavedListings()
  const { drafts: savedPlans } = useSavedPlans()
  const { drafts: savedPlates } = useKentekenDrafts()

  const fetchList = async (key, selection, sort, page) => {
    const response = await searchCarListings({ ...selection, mode: key }, page, PAGE_SIZE, sort)
    return { rows: response.data.map(toCard), total: response.totalCount, page }
  }

  const handleSearch = async (next, nextSort = sortBy) => {
    setCurrentView('search')
    setFilters(next); setSortBy(nextSort); setHasSearched(true); setIsSearching(true); setSearchError(false)
    const ticket = searchSession.current.begin(next, 1, nextSort)
    const keys = LISTS[next.mode === 'all' ? 'all' : 'picks']
    try {
      const loaded = await Promise.all(keys.map(key => fetchList(key, ticket.filters, nextSort, 1)))
      if (!searchSession.current.isCurrent(ticket)) return
      setLists(Object.fromEntries(keys.map((key, i) => [key, loaded[i]])))
    } catch (error) {
      if (!searchSession.current.isCurrent(ticket)) return
      console.error('Search failed:', error)
      setSearchError(true)
      setLists({})
    } finally {
      if (searchSession.current.isCurrent(ticket)) setIsSearching(false)
    }
  }

  // Run the default search once, so the first screen is the opportunities, not an empty form.
  useEffect(() => { handleSearch(emptyFilters()) }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async key => {
    const ticket = searchSession.current.current()
    const list = lists[key]
    if (!ticket || !list) return
    try {
      const next = await fetchList(key, ticket.filters, ticket.sortBy, list.page + 1)
      if (!searchSession.current.isCurrent(ticket)) return
      setLists(prev => ({ ...prev, [key]: { ...next, rows: [...prev[key].rows, ...next.rows] } }))
    } catch (error) {
      console.error('Loading more failed:', error)
      if (searchSession.current.isCurrent(ticket)) setSearchError(true)
    }
  }
  // The map plots every car of the shown lists (up to the all-pages cap), not just the loaded ones.
  const loadMapData = async () => {
    const ticket = searchSession.current.current()
    const keys = LISTS[ticket.filters.mode === 'all' ? 'all' : 'picks']
    return searchSession.current.map(ticket, async () => (await Promise.all(keys.map(key =>
      searchAllCarListings({ ...ticket.filters, mode: key }, ticket.sortBy)))).flat().map(toCard))
  }
  const shownLists = LISTS[filters.mode === 'all' ? 'all' : 'picks'].map(key => ({ key, ...(lists[key] || { rows: [], total: 0 }) }))

  const tab = (view, Icon, label, extra = null) => (
    <button className={`nav-btn ${currentView === view ? 'active' : ''}`} onClick={() => setCurrentView(view)}>
      <Icon className="nav-icon" /><span className="nav-label">{t(label)}{extra}</span>
    </button>
  )

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="header-title-section">
            <svg width="44" height="22" viewBox="0 0 80 40" xmlns="http://www.w3.org/2000/svg" className="header-arrow">
              <polygon points="0,20 22,2 22,12 58,12 58,2 80,20 58,38 58,28 22,28 22,38" fill="white"/>
            </svg>
            <div>
              <h1>AutoMargin <span className="header-subtitle"> · {t('import research')}</span></h1>
              <p>{t('European cars that are still cheaper than in the Netherlands after import')}</p>
            </div>
          </div>
          <button className="lang-toggle" onClick={toggleLang} aria-label="Switch language">
            {lang === 'nl' ? 'EN' : 'NL'}
          </button>
        </div>
      </header>

      <AppUpdateNotice />
      <nav className="app-nav">
        <div className="nav-content">
          {tab('search', FiSearch, 'Search')}
          {tab('kenteken', FiCreditCard, 'Kenteken Check')}
          {tab('analytics', FiBarChart2, 'Market Analytics')}
          {tab('scorecard', FiActivity, 'Scorecard')}
          {tab('saved', FiHeart, 'Saved Listings',
            <> <span className="nav-badge">{savedListings.length + savedPlans.length + savedPlates.length}</span></>)}
        </div>
      </nav>

      {currentView === 'disclaimer' ? (
        <DeferredSection key="disclaimer" onBack={() => setCurrentView('search')}>
          <LegalDisclaimer onBack={() => setCurrentView('search')} />
        </DeferredSection>
      ) : currentView === 'kenteken' ? (
        <DeferredSection key="kenteken" onBack={() => setCurrentView('search')}>
          <main className="app-main kenteken-view">
            <KentekenLookup initialDraft={plateDraft} onDraftConsumed={() => setPlateDraft(null)} />
          </main>
        </DeferredSection>
      ) : currentView === 'analytics' ? (
        <DeferredSection key="analytics" onBack={() => setCurrentView('search')}>
          <main className="app-main"><MarketAnalytics /></main>
        </DeferredSection>
      ) : currentView === 'scorecard' ? (
        <DeferredSection key="scorecard" onBack={() => setCurrentView('search')}>
          <main className="app-main"><Scorecard /></main>
        </DeferredSection>
      ) : currentView === 'saved' ? (
        <main className="app-main has-results">
          <div className="saved-listings-view">
            <SavedKentekenDrafts onResume={plate => { setPlateDraft(plate); setCurrentView('kenteken') }} />
            <SavedPlans />
            <ResultsList lists={[{ key: 'saved', rows: savedListings, total: savedListings.length }]}
              savedListingIds={savedListingIds} onToggleSave={toggleSaveListing} isSavedView />
          </div>
        </main>
      ) : (
        <main className={`app-main ${hasSearched ? 'has-results' : 'no-results'}`}>
          <div className="search-section">
            <SearchFilters filters={draft} setFilters={setDraft} onSearch={next => handleSearch({ ...next, mode: filters.mode })} />
          </div>
          <div className="results-section" aria-busy={isSearching}>
            {isSearching ? (
              <div className="loading" role="status" aria-live="polite"><div className="spinner"></div><p>{t('Searching...')}</p></div>
            ) : searchError ? (
              <section className="search-error" role="alert">
                <h2>{t('Search unavailable')}</h2>
                <p>{t('We could not load this selection. This does not mean no matching cars exist. Your submitted filters are kept.')}</p>
                <button type="button" onClick={() => handleSearch(filters)}>{t('Retry this search')}</button>
              </section>
            ) : hasSearched && (
              <ResultsList lists={shownLists} onLoadMore={loadMore}
                sortBy={sortBy} onSortChange={next => handleSearch(filters, next)}
                mode={filters.mode} onModeChange={mode => handleSearch({ ...filters, mode })}
                onLoadMapData={loadMapData} savedListingIds={savedListingIds} onToggleSave={toggleSaveListing} />
            )}
          </div>
        </main>
      )}

      <footer className="app-footer">
        <button onClick={() => { setCurrentView('disclaimer'); window.scrollTo({ top: 0 }) }}>
          {t('Legal Disclaimer & Terms of Use')}
        </button>
      </footer>
      <SavedListingFeedback feedback={feedback} onDismiss={dismissFeedback} />
    </div>
  )
}

export default App
